import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Clock, CurrencyDollar, Briefcase } from "@phosphor-icons/react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { getJobs } from "@/lib/api";
import { BUSINESS } from "@/lib/data";

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getJobs()
      .then(setJobs)
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="careers-page">
      <PageHero
        testId="careers-hero"
        eyebrow="Careers"
        lines={["Join the", "crew."]}
        subtitle={`We're always looking for reliable, honest people who take pride in their work. Explore open roles across ${BUSINESS.area}.`}
      />

      <section className="max-w-[1100px] mx-auto px-5 md:px-10 py-24">
        {loading ? (
          <p className="text-slate-500" data-testid="careers-loading">Loading open roles…</p>
        ) : jobs.length === 0 ? (
          <div data-testid="careers-empty" className="text-center py-16">
            <Briefcase size={40} weight="fill" className="text-navy-300 mx-auto" />
            <h2 className="font-display font-black text-2xl text-navy-900 mt-4">No open roles right now.</h2>
            <p className="text-slate-500 mt-2 max-w-md mx-auto">
              We don't have any openings at the moment — but we're always glad to meet good people.
              Reach out through our contact page and introduce yourself.
            </p>
            <Link
              to="/contact-us"
              data-testid="careers-empty-contact"
              className="inline-flex items-center gap-2 mt-6 rounded-full bg-navy-900 text-white px-6 py-3 font-bold hover:bg-navy-600 transition-colors"
            >
              Get in touch <ArrowUpRight weight="bold" />
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {jobs.map((job, i) => (
              <Reveal key={job.id} delay={i * 0.05}>
                <Link
                  to={`/careers/${job.id}`}
                  data-testid={`job-card-${i}`}
                  className="group block bg-white border border-slate-200 rounded-2xl p-7 hover:shadow-[0_12px_40px_rgb(10,17,40,0.08)] hover:-translate-y-0.5 transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-display font-black text-2xl text-navy-900 tracking-tight">{job.title}</h3>
                      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3 text-sm text-slate-600">
                        <span className="inline-flex items-center gap-1.5"><MapPin size={16} weight="fill" className="text-navy-500" />{job.location}</span>
                        <span className="inline-flex items-center gap-1.5"><Clock size={16} weight="fill" className="text-navy-500" />{job.job_type}</span>
                        {job.pay_range && (
                          <span className="inline-flex items-center gap-1.5"><CurrencyDollar size={16} weight="fill" className="text-navy-500" />{job.pay_range}</span>
                        )}
                      </div>
                    </div>
                    <span className="shrink-0 w-11 h-11 grid place-items-center rounded-full bg-navy-900 text-white group-hover:rotate-45 transition-transform">
                      <ArrowUpRight size={18} weight="bold" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
